// MIT License
// Copyright (c) 2023 - 2024 Jeet Mandaliya (Github Username: sereneinserenade)
// URL: https://github.com/sereneinserenade/tiptap-search-and-replace
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
// The above copyright notice and this permission notice shall be included in all
// copies or substantial portions of the Software.
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
// SOFTWARE.
import { Extension, Range, type Dispatch } from '@tiptap/core';
import { Decoration, DecorationSet } from '@tiptap/pm/view';
import { Plugin, PluginKey, type EditorState, type Transaction } from '@tiptap/pm/state';
import { Node as PMNode } from '@tiptap/pm/model';

export interface SearchAndReplaceStorage {
  searchTerm: string;
  replaceTerm: string;
  results: Range[];
  lastSearchTerm: string;
  caseSensitive: boolean;
  lastCaseSensitive: boolean;
  wholeWord: boolean;
  lastWholeWord: boolean;
  resultIndex: number;
  lastResultIndex: number;
}

// TipTap v3 module augmentation for type-safe storage access
declare module '@tiptap/core' {
  interface Storage {
    searchAndReplace: SearchAndReplaceStorage;
  }

  interface Commands<ReturnType> {
    search: {
      /**
       * @description Set search term in extension.
       */
      setSearchTerm: (searchTerm: string) => ReturnType;
      /**
       * @description Set replace term in extension.
       */
      setReplaceTerm: (replaceTerm: string) => ReturnType;
      /**
       * @description Set case sensitivity in extension.
       */
      setCaseSensitive: (caseSensitive: boolean) => ReturnType;
      /**
       * @description Set whole word match in extension.
       */
      setWholeWord: (wholeWord: boolean) => ReturnType;
      /**
       * @description Reset current search result to first instance.
       */
      resetIndex: () => ReturnType;
      /**
       * @description Find next instance of search result.
       */
      nextSearchResult: () => ReturnType;
      /**
       * @description Find previous instance of search result.
       */
      previousSearchResult: () => ReturnType;
      /**
       * @description Replace first instance of search result with given replace term.
       */
      replace: () => ReturnType;
      /**
       * @description Replace all instances of search result with given replace term.
       */
      replaceAll: () => ReturnType;
      /**
       * @description Clear search highlighting and reset search state.
       */
      clearSearch: () => ReturnType;
    };
  }
}
interface TextNodesWithPosition {
  text: string;
  pos: number;
}
const getRegex = (
  s: string,
  disableRegex: boolean,
  caseSensitive: boolean,
  wholeWord: boolean,
): RegExp => {
  // Escape special regex characters if regex is disabled
  const pattern = disableRegex ? s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') : s;

  // Add word boundaries if whole word matching is enabled
  const finalPattern = wholeWord ? `\\b${pattern}\\b` : pattern;

  return RegExp(finalPattern, caseSensitive ? 'gu' : 'gui');
};
interface ProcessedSearches {
  decorationsToReturn: DecorationSet;
  results: Range[];
}
function processSearches(
  doc: PMNode,
  searchTerm: RegExp,
  searchResultClass: string,
  resultIndex: number,
): ProcessedSearches {
  if (!searchTerm) {
    return {
      decorationsToReturn: DecorationSet.empty,
      results: [],
    };
  }

  const results: Range[] = [];
  const textNodesWithPosition: TextNodesWithPosition[] = [];
  let currentBlockIndex = -1;

  // Collect text nodes with their positions in a single pass
  doc.descendants((node, pos) => {
    if (node.isText && node.text) {
      const currentBlock = textNodesWithPosition[currentBlockIndex];
      if (currentBlock) {
        // Concatenate adjacent text nodes
        currentBlock.text += node.text;
      } else {
        textNodesWithPosition.push({ text: node.text, pos });
        currentBlockIndex = textNodesWithPosition.length - 1;
      }
    } else if (!node.isInline) {
      // Reset for new block-level nodes
      currentBlockIndex = -1;
    }
  });

  // Find matches in each text block
  for (const { text, pos } of textNodesWithPosition) {
    // Reset regex lastIndex to ensure fresh matching
    searchTerm.lastIndex = 0;
    let match: RegExpExecArray | null;

    while ((match = searchTerm.exec(text)) !== null) {
      const matchText = match[0];
      if (matchText && matchText.trim()) {
        results.push({
          from: pos + match.index,
          to: pos + match.index + matchText.length,
        });
      }
      // Prevent infinite loops for zero-length matches
      if (match.index === searchTerm.lastIndex) {
        searchTerm.lastIndex++;
      }
    }
  }

  // Create decorations in a single pass
  const currentClass = `${searchResultClass} ${searchResultClass}-current`;
  const decorations = results.map((r, i) =>
    Decoration.inline(r.from, r.to, {
      class: i === resultIndex ? currentClass : searchResultClass,
    }),
  );

  return {
    decorationsToReturn: DecorationSet.create(doc, decorations),
    results,
  };
}
const replace = (
  replaceTerm: string,
  results: Range[],
  { state, dispatch }: { state: EditorState; dispatch: Dispatch },
) => {
  const firstResult = results[0];
  if (!firstResult) return;
  const { from, to } = results[0];
  if (dispatch) dispatch(state.tr.insertText(replaceTerm, from, to));
};
const rebaseNextResult = (
  replaceTerm: string,
  index: number,
  lastOffset: number,
  results: Range[],
): [number, Range[]] | null => {
  const nextIndex = index + 1;
  if (!results[nextIndex]) return null;
  const { from: currentFrom, to: currentTo } = results[index];
  const offset = currentTo - currentFrom - replaceTerm.length + lastOffset;
  const { from, to } = results[nextIndex];
  results[nextIndex] = {
    to: to - offset,
    from: from - offset,
  };
  return [offset, results];
};
const replaceAll = (
  replaceTerm: string,
  results: Range[],
  { tr, dispatch }: { tr: Transaction; dispatch: Dispatch },
) => {
  let offset = 0;
  let resultsCopy = results.slice();
  if (!resultsCopy.length) return;
  for (let i = 0; i < resultsCopy.length; i += 1) {
    const { from, to } = resultsCopy[i];
    tr.insertText(replaceTerm, from, to);
    const rebaseNextResultResponse = rebaseNextResult(
      replaceTerm,
      i,
      offset,
      resultsCopy,
    );
    if (!rebaseNextResultResponse) continue;
    offset = rebaseNextResultResponse[0];
    resultsCopy = rebaseNextResultResponse[1];
  }
  if (dispatch) {
    dispatch(tr);
  }
};
export const searchAndReplacePluginKey = new PluginKey('searchAndReplacePlugin');
export interface SearchAndReplaceOptions {
  searchResultClass: string;
  disableRegex: boolean;
}
export const SearchAndReplace = Extension.create<
  SearchAndReplaceOptions,
  SearchAndReplaceStorage
>({
  name: 'searchAndReplace',
  addOptions() {
    return {
      searchResultClass: 'search-result',
      disableRegex: true,
    };
  },
  addStorage() {
    return {
      searchTerm: '',
      replaceTerm: '',
      results: [],
      lastSearchTerm: '',
      caseSensitive: false,
      lastCaseSensitive: false,
      wholeWord: false,
      lastWholeWord: false,
      resultIndex: 0,
      lastResultIndex: 0,
    };
  },
  addCommands() {
    return {
      setSearchTerm:
        (searchTerm: string) =>
        ({ editor }) => {
          editor.storage.searchAndReplace.searchTerm = searchTerm;
          return false;
        },
      setReplaceTerm:
        (replaceTerm: string) =>
        ({ editor }) => {
          editor.storage.searchAndReplace.replaceTerm = replaceTerm;
          return false;
        },
      setCaseSensitive:
        (caseSensitive: boolean) =>
        ({ editor }) => {
          editor.storage.searchAndReplace.caseSensitive = caseSensitive;
          return false;
        },
      setWholeWord:
        (wholeWord: boolean) =>
        ({ editor }) => {
          editor.storage.searchAndReplace.wholeWord = wholeWord;
          return false;
        },
      resetIndex:
        () =>
        ({ editor }) => {
          editor.storage.searchAndReplace.resultIndex = 0;
          return false;
        },
      nextSearchResult:
        () =>
        ({ editor }) => {
          const { results, resultIndex } = editor.storage.searchAndReplace;
          const nextIndex = resultIndex + 1;
          if (results[nextIndex]) {
            editor.storage.searchAndReplace.resultIndex = nextIndex;
          } else {
            editor.storage.searchAndReplace.resultIndex = 0;
          }
          return false;
        },
      previousSearchResult:
        () =>
        ({ editor }) => {
          const { results, resultIndex } = editor.storage.searchAndReplace;
          const prevIndex = resultIndex - 1;
          if (results[prevIndex]) {
            editor.storage.searchAndReplace.resultIndex = prevIndex;
          } else {
            editor.storage.searchAndReplace.resultIndex = results.length - 1;
          }
          return false;
        },
      replace:
        () =>
        ({ editor, state, dispatch }) => {
          const { replaceTerm, results } = editor.storage.searchAndReplace;
          replace(replaceTerm, results, { state, dispatch });
          return false;
        },
      replaceAll:
        () =>
        ({ editor, tr, dispatch }) => {
          const { replaceTerm, results } = editor.storage.searchAndReplace;
          replaceAll(replaceTerm, results, { tr, dispatch });
          return false;
        },
      clearSearch:
        () =>
        ({ editor }) => {
          const storage = editor.storage.searchAndReplace;
          // Clear the search term - the apply function will detect the change
          // and return DecorationSet.empty when searchTerm is empty
          storage.searchTerm = '';
          storage.replaceTerm = '';
          storage.results = [];
          storage.resultIndex = 0;
          // Note: Do NOT reset lastSearchTerm here - the apply function needs
          // to detect that searchTerm changed from a value to empty string
          // Trigger a transaction to force decoration update
          editor.view.dispatch(editor.state.tr);
          return true;
        },
    };
  },
  addProseMirrorPlugins() {
    const editor = this.editor;
    const { searchResultClass, disableRegex } = this.options;
    const setLastSearchTerm = (t: string) =>
      (editor.storage.searchAndReplace.lastSearchTerm = t);
    const setLastCaseSensitive = (t: boolean) =>
      (editor.storage.searchAndReplace.lastCaseSensitive = t);
    const setLastWholeWord = (t: boolean) =>
      (editor.storage.searchAndReplace.lastWholeWord = t);
    const setLastResultIndex = (t: number) =>
      (editor.storage.searchAndReplace.lastResultIndex = t);
    return [
      new Plugin({
        key: searchAndReplacePluginKey,
        state: {
          init: () => DecorationSet.empty,
          apply({ doc, docChanged }, oldState) {
            // Guard: ensure storage is initialized
            if (!editor.storage.searchAndReplace) {
              return oldState;
            }

            const {
              searchTerm,
              lastSearchTerm,
              caseSensitive,
              lastCaseSensitive,
              wholeWord,
              lastWholeWord,
              resultIndex,
              lastResultIndex,
            } = editor.storage.searchAndReplace;
            if (
              !docChanged &&
              lastSearchTerm === searchTerm &&
              lastCaseSensitive === caseSensitive &&
              lastWholeWord === wholeWord &&
              lastResultIndex === resultIndex
            )
              return oldState;
            setLastSearchTerm(searchTerm);
            setLastCaseSensitive(caseSensitive);
            setLastWholeWord(wholeWord);
            setLastResultIndex(resultIndex);
            if (!searchTerm) {
              editor.storage.searchAndReplace.results = [];
              return DecorationSet.empty;
            }
            const { decorationsToReturn, results } = processSearches(
              doc,
              getRegex(searchTerm, disableRegex, caseSensitive, wholeWord),
              searchResultClass,
              resultIndex,
            );
            editor.storage.searchAndReplace.results = results;
            return decorationsToReturn;
          },
        },
        props: {
          decorations(state) {
            return this.getState(state);
          },
        },
      }),
    ];
  },
});
export default SearchAndReplace;
